var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useState, useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { translate } from '@utils/i18n';
import Brushaway from '@components/Brushaway';
import { Button } from '@ui';
import { usePrepopulateQuery } from './hooks/usePrepopulateQuery';
import { useSprinklerSamplingMutation } from './hooks/useSprinklerSamplingMutation';
import { useGenerateKitNumberMutation } from './hooks/useGenerateKitNumberMutation';
import { SampleInformationStep, ShippingBillingStep } from './components/WizardSteps';
import ConfirmSubmissionModal from './components/ConfirmSubmissionModal';
import { NAMESPACE, PACKAGING_TYPE, MAX_SPRINKLER_LOCATIONS_DYNE_KIT, MAX_SPRINKLER_LOCATIONS_UNLIMITED } from './constants';
import './SprinklerSamplingIndex.scss';
const INITIAL_FORM_DATA = {
    packaging_type: PACKAGING_TYPE.DYNE_KIT,
    environment_type: 'standard',
    priority: 'standard',
    total_samples: 1,
    box_number: 1,
    total_boxes: 1,
    sprinkler_locations: [''],
    mail_physical_tag: false,
    bill_same_as_shipping: true, // Pay attention it is not sent to API
    send_results_to: {},
    send_bill_to: {}
};
function SprinklerSampleSubmissionForm({ buildingId, isOpen, onClose, initialSampleArea, initialLocation, initialHarshEnvironment }) {
    const [currentStep, setCurrentStep] = useState(1);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const { data: prepopulateData, isLoading: isLoadingDefaults, error: prepopulateError } = usePrepopulateQuery(buildingId);
    const createSubmission = useSprinklerSamplingMutation();
    const generateKitNumberMutation = useGenerateKitNumberMutation();
    const { control, handleSubmit, reset, trigger, setValue, formState: { errors } } = useForm({
        mode: 'onChange',
        defaultValues: INITIAL_FORM_DATA
    });
    const formValues = useWatch({ control });
    const getPrepopulatedFormData = (data) => ({
        samples_taken_by: data.jobInfo.samplesTakenBy,
        cell_phone: data.jobInfo.cellPhone,
        email: data.jobInfo.email,
        job_name: data.jobInfo.jobName,
        send_results_to: data.sendResultsTo,
        send_bill_to: data.sendBillTo
    });
    const getInitialSampleOverrides = () => {
        const overrides = {};
        if (initialSampleArea)
            overrides.sample_area = initialSampleArea;
        if (initialLocation)
            overrides.sprinkler_locations = [initialLocation];
        if (initialHarshEnvironment)
            overrides.environment_type = 'harsh_environment';
        return overrides;
    };
    useEffect(() => {
        if (prepopulateData) {
            reset(Object.assign(Object.assign(Object.assign({}, INITIAL_FORM_DATA), getPrepopulatedFormData(prepopulateData)), getInitialSampleOverrides()));
        }
    }, [prepopulateData, reset, initialSampleArea, initialLocation, initialHarshEnvironment]);
    useEffect(() => {
        if (!isOpen) {
            setCurrentStep(1);
        }
    }, [isOpen]);
    // Trim sprinkler locations when switching to Dyne Kit (limited to 8)
    useEffect(() => {
        const currentLocations = formValues.sprinkler_locations || [];
        const isDyneKit = formValues.packaging_type === PACKAGING_TYPE.DYNE_KIT;
        if (isDyneKit && currentLocations.length > MAX_SPRINKLER_LOCATIONS_DYNE_KIT) {
            setValue('sprinkler_locations', currentLocations.slice(0, MAX_SPRINKLER_LOCATIONS_DYNE_KIT));
        }
    }, [formValues.packaging_type, formValues.sprinkler_locations, setValue]);
    const validateStep1 = () => __awaiter(this, void 0, void 0, function* () {
        return yield trigger(['kit_number', 'samples_taken_by', 'email', 'total_samples', 'sprinkler_locations']);
    });
    const validateStep2 = () => __awaiter(this, void 0, void 0, function* () {
        const baseFields = [
            'send_results_to.name',
            'send_results_to.address',
            'send_results_to.city',
            'send_results_to.state',
            'send_results_to.zip',
            'send_results_to.email',
            'invoice_email'
        ];
        if (!formValues.bill_same_as_shipping) {
            return yield trigger([
                ...baseFields,
                'send_bill_to.name',
                'send_bill_to.address',
                'send_bill_to.city',
                'send_bill_to.state',
                'send_bill_to.zip',
                'send_bill_to.email'
            ]);
        }
        return yield trigger(baseFields);
    });
    const handleNext = () => __awaiter(this, void 0, void 0, function* () {
        const isStep1Valid = yield validateStep1();
        if (isStep1Valid) {
            setCurrentStep(2);
        }
    });
    const handleBack = () => {
        setCurrentStep(1);
    };
    const handleAddSprinklerLocation = () => {
        const currentLocations = formValues.sprinkler_locations || [];
        const isCustomerSuppliedBox = formValues.packaging_type === PACKAGING_TYPE.CUSTOMER_SUPPLIED_BOX;
        const maxLocations = isCustomerSuppliedBox ? MAX_SPRINKLER_LOCATIONS_UNLIMITED : MAX_SPRINKLER_LOCATIONS_DYNE_KIT;
        if (currentLocations.length < maxLocations) {
            reset(Object.assign(Object.assign({}, formValues), { sprinkler_locations: [...currentLocations, ''] }));
        }
    };
    const handleGenerateKitNumber = () => {
        generateKitNumberMutation.mutate(buildingId, {
            onSuccess: (response) => {
                var _a;
                if ((_a = response.data) === null || _a === void 0 ? void 0 : _a.kitNumber) {
                    setValue('kit_number', response.data.kitNumber, { shouldValidate: true });
                }
            }
        });
    };
    const handleRemoveSprinklerLocation = (index) => {
        const currentLocations = formValues.sprinkler_locations || [];
        if (currentLocations.length > 1) {
            const newLocations = currentLocations.filter((_, i) => i !== index);
            reset(Object.assign(Object.assign({}, formValues), { sprinkler_locations: newLocations }));
        }
    };
    const performSubmit = (data) => __awaiter(this, void 0, void 0, function* () {
        const submissionData = Object.assign({}, data);
        if (data.bill_same_as_shipping) {
            submissionData.send_bill_to = Object.assign({}, data.send_results_to);
        }
        // Filter out empty sprinkler locations
        if (submissionData.sprinkler_locations) {
            submissionData.sprinkler_locations = submissionData.sprinkler_locations.filter((location) => location && location.trim() !== '');
        }
        // Remove the UI-only field
        delete submissionData.bill_same_as_shipping;
        createSubmission.mutate({ buildingId, formData: submissionData }, {
            onSuccess: () => {
                if (prepopulateData) {
                    reset(Object.assign(Object.assign({}, INITIAL_FORM_DATA), getPrepopulatedFormData(prepopulateData)));
                }
                setShowConfirmModal(false);
                setCurrentStep(1);
                onClose();
            }
        });
    });
    const handleFormSubmit = () => __awaiter(this, void 0, void 0, function* () {
        if (currentStep === 1) {
            yield handleNext();
            return false;
        }
        const isStep2Valid = yield validateStep2();
        if (!isStep2Valid) {
            return false;
        }
        setShowConfirmModal(true);
        return false; // Don't close the brushaway yet
    });
    const handleClose = () => {
        setCurrentStep(1);
        onClose();
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(Brushaway, { open: isOpen, title: translate('form.title', { namespace: NAMESPACE, current_step: currentStep }), isForm: true, onSubmit: handleFormSubmit, customFooter: React.createElement("div", { className: "custom-footer-two-step" },
                currentStep === 2 && (React.createElement(Button, { onClick: handleBack, className: "footer-button-back" },
                    React.createElement("i", { className: "fa-light fa-arrow-left" }),
                    translate('back', { namespace: 'generic' }))),
                React.createElement("div", { className: "footer-buttons-right" },
                    React.createElement(Button, { onClick: handleClose }, translate('cancel', { namespace: 'generic' })),
                    React.createElement(Button, { variant: "primary", onClick: handleFormSubmit }, currentStep === 1 ? (React.createElement(React.Fragment, null,
                        React.createElement("i", { className: "fa-light fa-arrow-right" }),
                        translate('next', { namespace: 'generic' }))) : (translate('submit', { namespace: 'generic' }))))), onClose: handleClose }, isLoadingDefaults ? (React.createElement("div", { className: "workspace__section" },
            React.createElement("div", { className: "qmb-loading--96", "data-title": translate('loading', { namespace: 'generic' }) },
                React.createElement("svg", { role: "img" },
                    React.createElement("use", { href: "/map.svg#load-spinner" }))))) : (React.createElement(React.Fragment, null,
            prepopulateError && (React.createElement("div", { className: "workspace__section" },
                React.createElement("div", { className: "qmb-alert--warning" },
                    React.createElement("i", { className: "fa-light fa-exclamation-triangle alert__visual" }),
                    translate('form.error_loading_defaults', { namespace: NAMESPACE })))),
            React.createElement("form", { className: "qmb-form pb-0" },
                currentStep === 1 && (React.createElement(SampleInformationStep, { control: control, errors: errors, buildingAddress: prepopulateData === null || prepopulateData === void 0 ? void 0 : prepopulateData.building, onAddSprinklerLocation: handleAddSprinklerLocation, onRemoveSprinklerLocation: handleRemoveSprinklerLocation, onGenerateKitNumber: handleGenerateKitNumber, isGeneratingKitNumber: generateKitNumberMutation.isLoading })),
                currentStep === 2 && React.createElement(ShippingBillingStep, { control: control, errors: errors }))))),
        formValues && showConfirmModal && (React.createElement(ConfirmSubmissionModal, { isOpen: showConfirmModal, onClose: () => setShowConfirmModal(false), onConfirm: handleSubmit(performSubmit), isSubmitting: createSubmission.isLoading, formValues: formValues, prepopulateData: prepopulateData }))));
}
export default SprinklerSampleSubmissionForm;
